import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { useSpeedBackend } from '@/features/wallet';
import { queryKeys } from '@/lib/query';

import { useTradingStore } from '../store';

// Slower than the charted market's poll (which stays hot in useLivePosition):
// these are the positions the user is NOT looking at. The figures still have to
// move — a liquidation you cannot see coming is the accepted cost of Option A
// that this read exists to pay down — but they do not have to move at chart
// cadence.
const REFETCH_INTERVAL_MS = 5_000;
const STALE_TIME_MS = 3_000;

function num(value: string | null | undefined): number | null {
    if (value === null || value === undefined) return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Polls `GET /speed/positions` and merges every market's exchange figures into
 * the store's per-coin map.
 *
 * Before several positions could be open, one endpoint was enough: the client
 * polled the charted market and there was nothing else to know. Now a user can
 * hold five markets and watch one, and the other four need real
 * liquidation prices and real PnL — not the client's captured entry — or the
 * positions table is quietly showing stale figures for most of what they hold.
 *
 * This does NOT replace `useLivePosition`. That one polls the charted market
 * fast and drives the chart; this one covers everything at a calmer cadence.
 * Both write through `_syncLivePosition`, which is per-coin and keeps each
 * market's PnL anchor to itself.
 */
export function useAllLivePositions(): void {
    const backend = useSpeedBackend();
    const isRealMode = useTradingStore((s) => s.runtimeMode === 'real');
    const token = useTradingStore((s) => s.token);
    // No positions, no reason to poll. The active-trades list is the cheap
    // signal for that — it is already being fetched for the positions table.
    const hasPositions = useTradingStore(
        (s) => Object.keys(s.positionsByCoin).length > 0,
    );

    const enabled = isRealMode && !!token && !!backend && hasPositions;

    const { data } = useQuery({
        queryKey: queryKeys.speed.positions(),
        queryFn: async ({ signal }) => backend!.fetchPositions(signal),
        enabled,
        staleTime: STALE_TIME_MS,
        refetchInterval: enabled ? REFETCH_INTERVAL_MS : false,
    });

    useEffect(() => {
        if (!data) return;
        const store = useTradingStore.getState();
        const seen = new Set<string>();

        for (const position of data.positions) {
            const szi = num(position.szi);
            if (szi === null || szi === 0) continue;
            const key = position.coin.trim().toUpperCase();
            seen.add(key);
            const entryPx = num(position.entryPx);
            const unrealizedPnl = num(position.unrealizedPnl);
            const size = Math.abs(szi);
            store._syncLivePosition(position.coin, {
                entryPx,
                marginUsed: num(position.marginUsed),
                leverage: position.leverage?.value ?? null,
                unrealizedPnl,
                returnOnEquity: num(position.returnOnEquity),
                // clearinghouseState carries no per-position mark, so recover
                // the one its PnL was priced at — same derivation the charted
                // market uses in Trading.tsx.
                markPrice:
                    entryPx !== null && unrealizedPnl !== null && size !== 0
                        ? entryPx + unrealizedPnl / (szi < 0 ? -size : size)
                        : null,
                size,
                isLong: szi > 0,
                liquidationPx: num(position.liquidationPx),
            });
        }

        // A market the exchange no longer reports has no position on it. Clear
        // its snapshot so the table stops showing the last PnL it had — but only
        // the snapshot: whether the TRADE is gone is the active-trades list's
        // call, not this one's, and the two can disagree for a poll.
        for (const [key, slot] of Object.entries(store.positionsByCoin)) {
            if (seen.has(key) || slot.livePosition === null) continue;
            store._syncLivePosition(key, null);
        }
    }, [data]);
}
